import { CheckCircle2 } from 'lucide-react';
import type React from 'react';

import { Badge } from '~/components/ui/Badge';
import { Container } from '~/components/ui/Container';
import { Reveal } from '~/components/ui/Reveal';
import { SectionHeader } from '~/components/ui/SectionHeader';
import { cn } from '~/lib/cn';
import { useContent } from '~/hooks/useContent';

/** Rail Orange hex-pattern overlay — 4% opacity, used only in this section. */
function RailOrangeHexPattern(): React.ReactElement {
  const hexPath = 'M20,2 L38,11 L38,29 L20,38 L2,29 L2,11 Z';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="pointer-events-none select-none absolute inset-0 w-full h-full"
    >
      <defs>
        <pattern
          id="rail-orange-hex"
          x="0"
          y="0"
          width="60"
          height="52"
          patternUnits="userSpaceOnUse"
        >
          <path d={hexPath} fill="none" stroke="#FF6B2B" strokeWidth="1" opacity="0.04" />
          <path
            d={hexPath}
            transform="translate(30, 26)"
            fill="none"
            stroke="#FF6B2B"
            strokeWidth="1"
            opacity="0.04"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#rail-orange-hex)" />
    </svg>
  );
}

/**
 * RailwaySpecialisation — the ONLY section where Rail Orange is the dominant accent.
 * EN 45545 badge panel on the left (desktop) or top (mobile).
 * Bullet points on the right. HL1/HL2/HL3 chips at bottom.
 */
function RailwaySpecialisation(): React.ReactElement {
  const content = useContent();
  const { railway } = content;

  // Hazard Level chips
  const hlLevels = ['HL1', 'HL2', 'HL3'];

  return (
    <section
      id="railway"
      className={cn(
        'relative overflow-hidden',
        'bg-[var(--color-deep-panel)]',
        'py-24 md:py-32',
        'scroll-anchor'
      )}
      aria-labelledby="railway-heading"
    >
      {/* Rail Orange hex-pattern overlay — decorative */}
      <RailOrangeHexPattern />

      <Container className="relative z-10">
        {/* Section header — eyebrow in Rail Orange for this section only */}
        <Reveal>
          <div className="mb-14 md:mb-16 max-w-2xl">
            {/* Eyebrow — manually rendered in Rail Orange */}
            <span
              className={cn(
                'font-display font-semibold tracking-[0.22em] uppercase text-[0.6875rem]',
                'text-[var(--color-rail-orange)]',
                'block mb-3'
              )}
            >
              {railway.eyebrow}
            </span>

            <SectionHeader
              heading={railway.heading}
              lead={railway.lead}
              align="left"
              id="railway-heading"
            />
          </div>
        </Reveal>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Left column: EN 45545 badge panel */}
          <Reveal delay={80}>
            <div
              className={cn(
                'border-2 border-[var(--color-rail-orange)]',
                'rounded-sm p-8 md:p-10',
                'flex flex-col gap-4',
                // Subtle Rail Orange inner glow
                'shadow-[inset_0_0_40px_-12px_rgba(255,107,43,0.08)]'
              )}
            >
              {/* Large EN 45545 display */}
              <span
                className={cn(
                  'font-display font-extrabold uppercase',
                  'text-6xl md:text-7xl leading-none tracking-tight',
                  'text-[var(--color-rail-orange)]'
                )}
                aria-label={railway.badge}
              >
                {railway.badge}
              </span>

              {/* Sub-label */}
              <p
                className={cn(
                  'font-head font-semibold text-xl tracking-[0.05em]',
                  'text-[var(--color-frost-white)]'
                )}
              >
                Rolling stock fire standard
              </p>

              {/* Footnote */}
              <p
                className={cn(
                  'font-body font-light text-sm leading-[1.8]',
                  'text-[var(--color-steel-mist)]',
                  'mt-2'
                )}
              >
                {railway.badge} — European standard specifying fire protection requirements for
                materials used in railway rolling stock.
              </p>

              {/* Thin Rail Orange divider */}
              <span
                aria-hidden="true"
                className="block w-12 h-px bg-[var(--color-rail-orange)] opacity-50 mt-2"
              />
            </div>
          </Reveal>

          {/* Right column: bullet points */}
          <Reveal delay={140}>
            <ul className="flex flex-col gap-5" aria-label="Railway specialisation points">
              {railway.points.map((point) => (
                <li key={point} className="flex gap-4 items-start">
                  {/* Rail Orange chevron marker */}
                  <CheckCircle2
                    size={20}
                    strokeWidth={1.5}
                    className="flex-shrink-0 mt-0.5 text-[var(--color-rail-orange)]"
                    aria-hidden="true"
                  />
                  <span
                    className={cn(
                      'font-body font-light text-base leading-[1.8]',
                      'text-[var(--color-frost-white)]'
                    )}
                  >
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Bottom row: HL1 / HL2 / HL3 chips */}
        <Reveal delay={200} className="mt-12 md:mt-16">
          <div className="flex flex-wrap gap-3">
            {hlLevels.map((hl) => (
              <Badge key={hl} variant="railway">
                {hl}
              </Badge>
            ))}
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export { RailwaySpecialisation };
export default RailwaySpecialisation;
